<template>
  <div :class="classes">
    <table v-once :id="tableId" ref="table" :class="className" cellpadding="0">
      <thead :class="theadClassName">
        <tr>
          <th v-for="(field, i) in options.columns" :key="'head_' + i" :class="field.classHeaderName">
            <slot :name="'HEAD_' + field.name" :field="field" :i="i">
              <input v-if="field.name === '_select_checkbox'" type="checkbox" class="select-all-checkbox d-print-none">
              <div v-else v-html="field.label" />
            </slot>
          </th>
        </tr>
      </thead>
      <tfoot v-if="!hideTfoot" :class="tfootClassName">
        <tr>
          <th v-for="(field, i) in options.columns" :key="'foot_' + i" :class="field.classFooterName">
            <slot :name="'FOOT_' + field.name" :field="field" :i="i">
              <input v-if="columnSearch && (field.searchable || typeof field.searchable === 'undefined')" :placeholder="field.label" :class="columnSearchClassName" type="search">
              <div v-else-if="!columnSearch" v-html="field.label" />
            </slot>
          </th>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
// Reemplazo de 'vue-datatables-net' (niiknow/vue-datatables-net), que
// dejó de publicar una línea Vue2 bajo el nombre del paquete en npm —
// solo queda disponible en la rama 'vue2' del repo, sin releases. Esta
// es prácticamente una copia fiel de esa rama (src/VdtnetTable.vue),
// no una reescritura: 741 líneas ya probadas en producción durante
// años, portadas tal cual para no perder ningún caso de uso real.
//
// Cubre TODO lo que esta app usa (confirmado grepeando los 43 archivos
// que la consumen antes de tocar nada): fields->columns, selectCheckbox
// (los 43), render (39/43), data-action (27/43), reload/search/
// getServerParams vía $refs, y los eventos table-creating/table-created/
// reloaded/row-select/row-deselect.
//
// A propósito, NO porté: compileTemplate (fields.template / scoped
// slots), el patrón master-details (prop `details`), dataLoader (datos
// locales en vez de ajax), ni el footer de columnSearch — cero usos de
// esas cuatro cosas en los 43 archivos reales. Si algún día se
// necesitan, están documentadas en la fuente original (niiknow/
// vue-datatables-net, rama vue2, src/VdtnetTable.vue).
let myUniqueId = 1;

export default {
  name: 'VdtnetTable',
  props: {
    id: { type: String, default: null },
    containerClassName: { type: String, default: 'table-responsive d-print-inline' },
    columnSearchClassName: { type: String, default: 'form-control form-control-sm' },
    tfootClassName: { type: String },
    theadClassName: { type: String },
    className: { type: String, default: 'table table-striped table-bordered nowrap w-100' },
    opts: { type: Object },
    fields: { type: Object },
    jquery: { type: Function },
    selectCheckbox: { type: Number, default: -1 },
    hideFooter: { type: Boolean },
    hideTfoot: { type: Boolean, default: true },
    columnSearch: { type: Boolean, default: false },
  },
  data() {
    return {
      tableId: null,
      options: {
        /* eslint-disable */
        dom: "tr<'row vdtnet-footer'<'col-sm-12 col-md-5'i><'col-sm-12 col-md-7'pl>>",
        /* eslint-enable */
        columns: [],
        language: {
          infoFiltered: '',
        },
        lengthMenu: [[15, 100, 500, 1000, -1], [15, 100, 500, 1000, 'All']],
        pageLength: 15,
        buttons: [],
      },
      dataTable: null,
      vdtnet: this,
    };
  },
  computed: {
    jq() {
      return this.jquery || window.jQuery;
    },
    classes() {
      let classes = `${this.containerClassName} vdtnet-container`;
      if (this.hideFooter) {
        classes += ' hide-footer';
      }
      return classes;
    },
  },
  created() {
    const that = this;
    const jq = that.jq;

    let startCol = 0;
    let icol = 0;

    that.tableId = that.id || `vdtnetable${myUniqueId++}`;

    if (that.opts) {
      that.options = jq.extend({}, that.options, that.opts);
    }

    that.options.order = that.options.order || [[startCol, 'asc']];

    if (that.fields) {
      const fields = that.fields;
      const cols = that.options.columns;
      const orders = that.options.order;

      for (const k in fields) {
        const field = fields[k];
        field.name = field.name || k;

        if (field.isLocal) {
          field.searchable = false;
          field.sortable = false;
        }

        const col = {
          label: field.label || field.name,
          data: field.data || field.name,
          width: field.width,
          name: field.name,
          className: field.className,
          index: field.index || icol + 1,
        };

        if (field.hasOwnProperty('defaultContent')) {
          col.defaultContent = field.defaultContent;
        }
        if (field.hasOwnProperty('sortable')) {
          col.orderable = field.sortable;
        }
        if (field.hasOwnProperty('visible')) {
          col.visible = field.visible;
        }
        if (field.hasOwnProperty('searchable')) {
          col.searchable = field.searchable;
        }
        if (field.hasOwnProperty('classHeaderName')) {
          col.classHeaderName = field.classHeaderName;
        }
        if (field.hasOwnProperty('classFooterName')) {
          col.classFooterName = field.classFooterName;
        }
        if (field.render) {
          col.render = field.render;
        }

        cols.push(col);

        if (field.defaultOrder) {
          orders.push([icol, field.defaultOrder]);
        }

        icol++;
      }
    }

    if (that.selectCheckbox) {
      const col = {
        orderable: false,
        searchable: false,
        name: '_select_checkbox',
        className: 'select-checkbox d-print-none',
        data: null,
        defaultContent: '',
        index: that.selectCheckbox - 1,
      };
      that.options.columns.splice(that.selectCheckbox - 1, 0, col);
      that.options.select = that.options.select || { style: 'os', selector: 'td.select-checkbox' };

      if (that.selectCheckbox === 1) {
        startCol++;
      }
    }

    if (startCol > 0) {
      if (that.options.order) {
        that.options.order.forEach((v) => {
          v[0] += startCol;
        });
      } else {
        that.options.order = [[startCol, 'asc']];
      }
    }
  },
  mounted() {
    const that = this;
    const jq = that.jq;
    const $el = jq(that.$refs.table);
    const cols = that.options.columns;

    for (const k in cols) {
      const col = cols[k];

      if (col.render) {
        const myRender = col.render;
        col.render = function () {
          return myRender.apply(that, Array.prototype.slice.call(arguments));
        };
      }
    }

    that.$emit('table-creating', that, $el);

    that.dataTable = $el.DataTable(that.options);

    if (that.selectCheckbox) {
      $el.on('click', 'th input.select-all-checkbox', (e) => {
        if (jq(e.target).is(':checked')) {
          that.dataTable.rows().select();
        } else {
          that.dataTable.rows().deselect();
        }
      });

      that.dataTable.on('select deselect', (e, dt, type, indexes) => {
        const $input = $el.find('th input.select-all-checkbox');
        if (that.dataTable.rows({ selected: true }).count() !== that.dataTable.rows().count()) {
          jq('th.select-checkbox').removeClass('selected');
          $input.attr('checked', false);
        } else {
          jq('th.select-checkbox').addClass('selected');
          $input.attr('checked', true);
        }

        that.$emit('row-' + e.type, {
          dataTable: that.dataTable,
          e: e,
          dt: dt,
          type: type,
          indexes: indexes,
        });
      });
    }

    $el.on('remove', () => {
      if (that.dataTable) {
        that.dataTable.destroy(true);
      }
      that.dataTable = null;
    });

    // Row action buttons: data-action="edit" (etc.) en cualquier elemento
    // dentro de una fila dispara un evento con ese nombre en el componente.
    $el.on('click', '[data-action]', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const target = jq(e.currentTarget);
      const action = target.attr('data-action');

      if (!action) {
        return;
      }

      let tr = target;
      if (target.prop('tagName') !== 'TR') {
        tr = target.closest('tr');
      }

      if (tr.length) {
        const row = that.dataTable.row(tr);
        const data = row.data();
        that.$emit(action, data, row, tr, target);
      } else {
        that.$emit(action, null, null, null, target);
      }
    });

    that.$emit('table-created', that);
  },
  methods: {
    reload(resetPaging = false) {
      const that = this;
      that.dataTable.ajax.reload((data) => {
        that.$emit('reloaded', data, that);
      }, resetPaging);
      return that;
    },
    search(value) {
      this.dataTable.search(value).draw();
      return this;
    },
    getServerParams() {
      return this.dataTable.ajax.params();
    },
  },
};
</script>

<style>
.select-checkbox, .select-all-checkbox {
  cursor: pointer;
}
.vdtnet-footer .dataTables_length {
  padding-top: 6px;
  padding-right: 10px;
}
.vdtnet-footer .dataTables_length, .vdtnet-footer .dataTables_paginate {
  float: right;
}
.hide-footer .vdtnet-footer {
  display: none;
}
</style>
